/**
 * Topbar — sticks to the top of the main content column.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ [☰] [⫷] Workspace › Dashboard            [📖] [🔔] [⚙]    │
 *   └────────────────────────────────────────────────────────────┘
 *
 *   ☰  — mobile drawer toggle (<lg)
 *   ⫷  — desktop sidebar mode cycle: open → icons → closed (≥lg)
 *   📖 — Knowledge Base (public page, also reachable signed-out)
 *   🔔 — notifications (live)
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { getRolePrefix } from '../../utils/roleUtils.js';
import Icon from '../ui/Icons.jsx';
import NotificationsBell from './NotificationsBell.jsx';

const MODE_LABEL = {
  open: 'Collapse sidebar to icons',
  icons: 'Hide sidebar',
  closed: 'Show sidebar',
};

export default function Topbar({
  title = 'Workspace',
  sectionLabel = 'Workspace',
  onMenuClick,
  onSidebarCycle,
  sidebarMode = 'open',
}) {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const goToSettings = () => {
    if (!profile) return;
    navigate(`${getRolePrefix(profile.role)}/settings`);
  };

  return (
    <header
      className="h-[60px] flex items-center gap-2 sm:gap-3 px-4 sm:px-7 sticky top-0 z-10 border-b"
      style={{
        background: 'var(--card)',
        borderColor: 'var(--border)',
      }}
    >
      {/* Drawer toggle — mobile/tablet only */}
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          data-tour="nav-toggle"
          className="lg:hidden w-[34px] h-[34px] rounded-md border grid place-items-center hover:bg-slate-50 transition-colors flex-shrink-0"
          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          aria-label="Open navigation"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      {/* Sidebar mode cycle — desktop only */}
      {onSidebarCycle && (
        <button
          type="button"
          onClick={onSidebarCycle}
          className="hidden lg:grid w-[34px] h-[34px] rounded-md border place-items-center hover:bg-slate-50 transition-colors flex-shrink-0"
          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          title={MODE_LABEL[sidebarMode] || 'Toggle sidebar'}
          aria-label={MODE_LABEL[sidebarMode] || 'Toggle sidebar'}
        >
          <Icon name="panel" size={16} className="text-ink" />
        </button>
      )}

      {/* Breadcrumb */}
      <div className="text-sm font-medium flex items-center min-w-0">
        <span className="text-muted hidden sm:inline">{sectionLabel}</span>
        <Icon name="chev" size={14} className="mx-1.5 text-slate-400 hidden sm:inline" />
        <span className="text-ink truncate">{title}</span>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {/* Knowledge base — public help center */}
        <button
          type="button"
          onClick={() => navigate('/knowledgebase')}
          className="w-[34px] h-[34px] rounded-md border grid place-items-center hover:bg-slate-50 transition-colors flex-shrink-0"
          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          title="Knowledge Base"
          aria-label="Open knowledge base"
        >
          <Icon name="book" size={16} className="text-ink" />
        </button>

        {/* Notifications — live */}
        <NotificationsBell />

        {/* Settings */}
        <button
          type="button"
          onClick={goToSettings}
          data-tour="settings"
          className="w-[34px] h-[34px] rounded-md border grid place-items-center hover:bg-slate-50 transition-colors flex-shrink-0"
          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          title="Settings"
          aria-label="Open settings"
        >
          <Icon name="settings" size={16} className="text-ink" />
        </button>
      </div>
    </header>
  );
}
